import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useSocketContext } from '../../context/SocketContext';
import cn from '../../utils/cn';

const MAX = 20;

const TYPE_STYLES = {
  order:   { bg: 'bg-blue-50',   dot: 'bg-blue-500',   icon: '📦' },
  payment: { bg: 'bg-green-50',  dot: 'bg-green-500',  icon: '💳' },
  seller:  { bg: 'bg-purple-50', dot: 'bg-purple-500', icon: '🏪' },
  default: { bg: 'bg-gray-50',   dot: 'bg-gray-400',   icon: '🔔' },
};

const formatTime = (d) => {
  const diff = Date.now() - new Date(d).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1)  return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  return new Date(d).toLocaleDateString('en-PK', { day: 'numeric', month: 'short' });
};

const NotificationBell = () => {
  const [open, setOpen]               = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread]           = useState(0);
  const ref = useRef(null);
  const socket = useSocketContext();

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Listen to socket events
  useEffect(() => {
    if (!socket) return;

    const addNotification = (n) => {
      setNotifications(prev => [n, ...prev].slice(0, MAX));
      setUnread(u => u + 1);
    };

    socket.on('order:statusUpdated', (data) => {
      const statusLabel = {
        confirmed: 'confirmed', processing: 'being processed',
        shipped: 'shipped', delivered: 'delivered', cancelled: 'cancelled',
      };
      addNotification({
        id: `${data.orderId}-${Date.now()}`,
        type: 'order',
        title: 'Order Update',
        message: `Order ${data.orderNumber} is now ${statusLabel[data.status] || data.status}`,
        link: `/orders/${data.orderId}`,
        time: new Date(),
      });
    });

    socket.on('payment:updated', (data) => {
      addNotification({
        id: `pay-${data.orderId}-${Date.now()}`,
        type: 'payment',
        title: data.paymentStatus === 'paid' ? 'Payment Confirmed' : 'Payment Failed',
        message: data.paymentStatus === 'paid'
          ? 'Your payment was successful. Order is being processed.'
          : 'Your payment failed. Please try again.',
        link: `/orders/${data.orderId}`,
        time: new Date(),
      });
    });

    return () => {
      socket.off('order:statusUpdated');
      socket.off('payment:updated');
    };
  }, [socket]);

  const markAllRead = () => setUnread(0);
  const clearAll    = () => { setNotifications([]); setUnread(0); };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => { setOpen(o => !o); if (!open) markAllRead(); }}
        className="relative p-2 text-gray-600 hover:text-primary-600 transition-colors rounded-lg hover:bg-gray-100"
        aria-label="Notifications"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold leading-none">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-80 bg-white rounded-2xl shadow-xl border border-gray-100 z-20 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
              {notifications.length > 0 && (
                <button onClick={clearAll} className="text-xs text-gray-400 hover:text-gray-600 transition-colors">
                  Clear all
                </button>
              )}
            </div>

            {/* List */}
            <div className="max-h-80 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-center">
                  <svg className="w-10 h-10 text-gray-200 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                      d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  <p className="text-sm text-gray-400">No notifications yet</p>
                  <p className="text-xs text-gray-300 mt-0.5">Order updates will appear here</p>
                </div>
              ) : (
                notifications.map((n) => {
                  const style = TYPE_STYLES[n.type] || TYPE_STYLES.default;
                  const inner = (
                    <div className={cn('flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors', style.bg)}>
                      <span className="text-lg shrink-0 mt-0.5">{style.icon}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-gray-800">{n.title}</p>
                        <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{n.message}</p>
                        <p className="text-xs text-gray-400 mt-1">{formatTime(n.time)}</p>
                      </div>
                    </div>
                  );
                  return n.link
                    ? <Link key={n.id} to={n.link} onClick={() => setOpen(false)} className="block no-underline border-b border-gray-50 last:border-0">{inner}</Link>
                    : <div key={n.id} className="border-b border-gray-50 last:border-0">{inner}</div>;
                })
              )}
            </div>

            {/* Footer */}
            {notifications.length > 0 && (
              <div className="px-4 py-2.5 border-t border-gray-100 text-center">
                <Link to="/orders" onClick={() => setOpen(false)}
                  className="text-xs text-primary-600 hover:text-primary-700 font-medium no-underline">
                  View all orders →
                </Link>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationBell;
